import type { ReactNode, RefObject } from 'react';
import { SiteFooter } from './SiteFooter';
import { SiteHeader } from './SiteHeader';
import { MAIN_CONTENT_ID, SkipToContent } from './SkipToContent';

/**
 * The Experience Shell — the public website.
 *
 * Unlike the Application Shell it applies no content width of its own. Public
 * pages read as a vertical sequence of full-width bands, each constraining its
 * own measure, so a shell that imposed `max-w-3xl` would make a full-bleed band
 * impossible. The header and footer supply the page's only shell-level widths,
 * both `max-w-6xl` per section D.1 of the visual system.
 *
 * This slice delivers the frame only. No homepage, no Why HPOS, no assessment
 * marketing pages, and no copy: those are the later V2-018C page slices, and
 * until they land the public paths render the existing not-found fallback
 * inside this frame.
 *
 * The main-landmark ref is supplied by the caller rather than created here. It
 * has to outlive this component: a navigation that crosses the shell boundary
 * unmounts this shell and mounts the other, and route-focus state held inside
 * either one would reset on exactly that transition.
 */
export function ExperienceShell({
  children,
  mainRef,
}: {
  children: ReactNode;
  mainRef: RefObject<HTMLElement | null>;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <SkipToContent />
      <SiteHeader variant="public" />
      <main
        id={MAIN_CONTENT_ID}
        ref={mainRef}
        tabIndex={-1}
        className="w-full flex-1 focus-visible:outline-none"
      >
        {children}
      </main>
      <SiteFooter variant="public" />
    </div>
  );
}
